import React, {
    useState,
    useEffect,
    useLayoutEffect,
    useRef,
    useMemo,
    useCallback,
} from "react";
import { Link, useNavigate } from "react-router-dom";
import { useMsal } from "@azure/msal-react";
import { useData } from "../../providers/DataProvider";
import logo from "../../assets/images/logo-white.png";
import { SignOutButton } from "../Buttons/SignOutButton";
import RegionSwitcher from "../RegionSwitcher";
import BonusTypeSwitcher from "../BonusTypeSwitcher";
import { Bars3Icon } from "@heroicons/react/24/outline";
import RegionLoadingBar from "../RegionLoadingBar/RegionLoadingBar";

interface TokenClaims {
    roles?: string[];
    [key: string]: unknown;
}

interface HeaderProps {
    onMenuToggle?: () => void;
}

type FocusTarget = "desktopRole" | "mobileRole" | "options" | null;

const Header: React.FC<HeaderProps> = ({ onMenuToggle }) => {
    const { accounts } = useMsal();
    const navigate = useNavigate();
    const { setRole, role, loadDataForRole, bonusType, setBonusType, isRoleLoading } = useData();

    const [isOptionsOpen, setIsOptionsOpen] = useState(false);
    const [isRoleMenuOpenDesktop, setIsRoleMenuOpenDesktop] = useState(false);
    const [isRoleMenuOpenMobile, setIsRoleMenuOpenMobile] = useState(false);

    const optionsMenuRef = useRef<HTMLDivElement>(null);
    const roleMenuDesktopRef = useRef<HTMLDivElement>(null);
    const roleMenuMobileRef = useRef<HTMLDivElement>(null);

    const roleTriggerDesktopRef = useRef<HTMLButtonElement>(null);
    const roleTriggerMobileRef = useRef<HTMLButtonElement>(null);
    const optionsTriggerRef = useRef<HTMLButtonElement>(null);

    const roleDropdownDesktopRef = useRef<HTMLDivElement>(null);
    const roleDropdownMobileRef = useRef<HTMLDivElement>(null);

    const isOptionsOpenRef = useRef(isOptionsOpen);
    const isRoleMenuOpenDesktopRef = useRef(isRoleMenuOpenDesktop);
    const isRoleMenuOpenMobileRef = useRef(isRoleMenuOpenMobile);

    useLayoutEffect(() => { isOptionsOpenRef.current = isOptionsOpen; }, [isOptionsOpen]);
    useLayoutEffect(() => { isRoleMenuOpenDesktopRef.current = isRoleMenuOpenDesktop; }, [isRoleMenuOpenDesktop]);
    useLayoutEffect(() => { isRoleMenuOpenMobileRef.current = isRoleMenuOpenMobile; }, [isRoleMenuOpenMobile]);

    const lastFocusTargetRef = useRef<FocusTarget>(null);

    const closeDesktopRoleMenu = useCallback(() => {
        setIsRoleMenuOpenDesktop(false);
        roleTriggerDesktopRef.current?.focus();
        lastFocusTargetRef.current = null;
    }, []);

    const closeMobileRoleMenu = useCallback(() => {
        setIsRoleMenuOpenMobile(false);
        roleTriggerMobileRef.current?.focus();
        lastFocusTargetRef.current = null;
    }, []);

    const closeAllMenus = useCallback(({ restoreFocus = true }: { restoreFocus?: boolean } = {}) => {
        setIsOptionsOpen(false);
        setIsRoleMenuOpenDesktop(false);
        setIsRoleMenuOpenMobile(false);

        if (restoreFocus) {
            const target = lastFocusTargetRef.current;
            if (target === "desktopRole") roleTriggerDesktopRef.current?.focus();
            else if (target === "mobileRole") roleTriggerMobileRef.current?.focus();
            else if (target === "options") optionsTriggerRef.current?.focus();
        }
        lastFocusTargetRef.current = null;
    }, []);

    // Outside click
    useEffect(() => {
        const handlePointerDown = (event: PointerEvent) => {
            const target = event.target as Node;
            const inOptions       = optionsMenuRef.current?.contains(target)       ?? false;
            const inRoleDesktop   = roleMenuDesktopRef.current?.contains(target)   ?? false;
            const inRoleMobile    = roleMenuMobileRef.current?.contains(target)    ?? false;

            if (!inOptions && !inRoleDesktop && !inRoleMobile) {
                if (isRoleMenuOpenDesktopRef.current) lastFocusTargetRef.current = "desktopRole";
                else if (isRoleMenuOpenMobileRef.current) lastFocusTargetRef.current = "mobileRole";
                else if (isOptionsOpenRef.current) lastFocusTargetRef.current = "options";
                closeAllMenus();
                return;
            }

            if (!inRoleDesktop) setIsRoleMenuOpenDesktop(false);
            if (!inRoleMobile)  setIsRoleMenuOpenMobile(false);
            if (!inOptions)     setIsOptionsOpen(false);
        };
        window.addEventListener("pointerdown", handlePointerDown);
        return () => window.removeEventListener("pointerdown", handlePointerDown);
    }, [closeAllMenus]);

    // Escape
    useEffect(() => {
        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key !== "Escape") return;
            if (isRoleMenuOpenDesktopRef.current) lastFocusTargetRef.current = "desktopRole";
            else if (isRoleMenuOpenMobileRef.current) lastFocusTargetRef.current = "mobileRole";
            else if (isOptionsOpenRef.current) lastFocusTargetRef.current = "options";
            closeAllMenus();
        };
        window.addEventListener("keydown", onKeyDown);
        return () => window.removeEventListener("keydown", onKeyDown);
    }, [closeAllMenus]);

    // Focus first item on open
    useEffect(() => {
        if (isRoleMenuOpenDesktop && roleDropdownDesktopRef.current) {
            roleDropdownDesktopRef.current.querySelector<HTMLElement>('[role="menuitem"]')?.focus();
        }
    }, [isRoleMenuOpenDesktop]);

    useEffect(() => {
        if (isRoleMenuOpenMobile && roleDropdownMobileRef.current) {
            roleDropdownMobileRef.current.querySelector<HTMLElement>('[role="menuitem"]')?.focus();
        }
    }, [isRoleMenuOpenMobile]);

    const handleRoleSelect = useCallback(
        async (selectedRole: string) => {
            setRole(selectedRole);
            lastFocusTargetRef.current = null;
            closeAllMenus({ restoreFocus: false });
            await loadDataForRole(selectedRole);
            navigate("/", { replace: true });
        },
        [setRole, loadDataForRole, navigate, closeAllMenus]
    );

    const rolesFromToken = (accounts?.[0]?.idTokenClaims as TokenClaims)?.roles;

    const filteredRoles = useMemo(() => {
        const roles = rolesFromToken ?? [];
        return roles
            .filter((r) => {
                if (r === "Admin") return false;
                if (bonusType !== "ClosingBonus" && (r === "Ops" || r === "PM" || r === "Uploader")) return false;
                return true;
            })
            .sort((a, b) => a.localeCompare(b));
    }, [rolesFromToken, bonusType]);

    useEffect(() => {
        if (role || filteredRoles.length === 0) return;
        const firstRole = filteredRoles[0];
        setRole(firstRole);
        loadDataForRole(firstRole);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filteredRoles]);

    // Arrow-key handler factory
    const makeMenuKeyDownHandler = useCallback(
        (dropdownRef: React.RefObject<HTMLDivElement>, closeFn: () => void) =>
            (e: React.KeyboardEvent<HTMLElement>) => {
                const root = dropdownRef.current;
                if (!root) return;
                const items = Array.from(root.querySelectorAll<HTMLElement>('[role="menuitem"]'));
                if (!items.length) return;
                const active = document.activeElement as HTMLElement | null;
                const currentIndex = active ? items.indexOf(active) : -1;
                switch (e.key) {
                    case "Escape":    e.preventDefault(); closeFn(); break;
                    case "ArrowDown": e.preventDefault(); items[(currentIndex + 1 + items.length) % items.length]?.focus(); break;
                    case "ArrowUp":   e.preventDefault(); items[(currentIndex - 1 + items.length) % items.length]?.focus(); break;
                    case "Home":      e.preventDefault(); items[0]?.focus(); break;
                    case "End":       e.preventDefault(); items[items.length - 1]?.focus(); break;
                    case "Tab":       closeFn(); break;
                }
            },
        []
    );

    const handleDesktopRoleKeyDown = useMemo(
        () => makeMenuKeyDownHandler(roleDropdownDesktopRef, closeDesktopRoleMenu),
        [makeMenuKeyDownHandler, closeDesktopRoleMenu]
    );

    const handleMobileRoleKeyDown = useMemo(
        () => makeMenuKeyDownHandler(roleDropdownMobileRef, closeMobileRoleMenu),
        [makeMenuKeyDownHandler, closeMobileRoleMenu]
    );

    // Shared spinner + chevron label (used in both desktop and mobile role buttons)
    const RoleLabel = ({ isMenuOpen }: { isMenuOpen: boolean }) =>
        isRoleLoading ? (
            <>
                <svg
                    className="animate-spin w-4 h-4 mr-2 text-white"
                    fill="none" viewBox="0 0 24 24" aria-hidden="true"
                >
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 00-8 8h4z" />
                </svg>
                <span>Loading…</span>
            </>
        ) : (
            <>
                <span className="mr-2">{role || "Select Role"}</span>
                <svg
                    className={`w-4 h-4 transition-transform duration-200 ${isMenuOpen ? "rotate-180" : ""}`}
                    fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true"
                >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
            </>
        );

    return (
        <nav className="bg-primary-500 border-b border-gray-200 px-4 py-2.5 fixed left-0 right-0 top-0 z-50">
            <RegionLoadingBar />
            <div className="flex flex-wrap justify-between items-center">

                {/* Left: Hamburger (desktop-only toggle) + Logo */}
                <div className="flex items-center gap-3">
                    {/* Hamburger — only shown on desktop sidebar breakpoint; on mobile the bottom bar handles nav */}
                    <button
                        onClick={onMenuToggle}
                        className="lg:hidden p-1.5 rounded-lg text-white hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-white"
                        aria-label="Open menu"
                    >
                        <Bars3Icon className="w-6 h-6" />
                    </button>

                    <Link
                        to="/"
                        className="flex items-center mr-2"
                        onClick={() => closeAllMenus({ restoreFocus: false })}
                    >
                        <img src={logo} className="mr-2 h-9 sm:h-11" alt="Logo" />
                        <div className="hidden sm:flex flex-col leading-tight">
                            <span className="text-lg sm:text-xl font-semibold whitespace-nowrap text-white">
                                Construction Bonus
                            </span>
                            <span className="text-white/50 text-[10px] font-medium tracking-widest uppercase">
                                Highland Homes
                            </span>
                        </div>
                    </Link>
                </div>

                {/* Right: Controls */}
                <div className="flex items-center lg:order-2 gap-2">

                    {/* Region + BonusType — desktop ≥ 840px (RegionSwitcher/BonusTypeSwitcher use useData internally) */}
                    <div className="hidden [@media(min-width:840px)]:block">
                        <RegionSwitcher />
                    </div>
                    <div className="hidden [@media(min-width:840px)]:block">
                        <BonusTypeSwitcher
                            onBonusTypeChange={(newType) => {
                                if (newType !== bonusType) setBonusType(newType);
                            }}
                        />
                    </div>

                    {/* Desktop Role dropdown — ≥ 840px */}
                    <div className="hidden [@media(min-width:840px)]:block relative" ref={roleMenuDesktopRef}>
                        <button
                            ref={roleTriggerDesktopRef}
                            onClick={() => {
                                if (isRoleLoading) return;
                                lastFocusTargetRef.current = "desktopRole";
                                setIsRoleMenuOpenDesktop((v) => !v);
                                setIsRoleMenuOpenMobile(false);
                                setIsOptionsOpen(false);
                            }}
                            disabled={isRoleLoading}
                            className="flex items-center justify-center bg-primary-600 hover:bg-primary-700 text-white font-medium py-2 px-4 rounded-md transition duration-150 ease-in-out disabled:opacity-75 disabled:cursor-not-allowed"
                            aria-haspopup="menu"
                            aria-expanded={isRoleMenuOpenDesktop}
                            aria-controls="role-menu-desktop"
                            aria-busy={isRoleLoading}
                        >
                            <RoleLabel isMenuOpen={isRoleMenuOpenDesktop} />
                        </button>

                        {isRoleMenuOpenDesktop && (
                            <div
                                id="role-menu-desktop"
                                ref={roleDropdownDesktopRef}
                                role="menu"
                                className="absolute right-0 mt-2 py-2 w-48 bg-white rounded-md shadow-xl z-20"
                            >
                                {filteredRoles.map((r) => (
                                    <button
                                        key={r}
                                        onClick={() => handleRoleSelect(r)}
                                        onKeyDown={handleDesktopRoleKeyDown}
                                        className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 w-full text-left transition duration-150 ease-in-out"
                                        role="menuitem"
                                        tabIndex={-1}
                                    >
                                        {r}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Options menu — < 840px (mobile: no bottom-bar dropdowns here, this is the < 840px fallback for non-mobile-nav users) */}
                    <div className="[@media(min-width:840px)]:hidden relative" ref={optionsMenuRef}>
                        <button
                            ref={optionsTriggerRef}
                            onClick={() => {
                                lastFocusTargetRef.current = "options";
                                setIsOptionsOpen((v) => !v);
                                setIsRoleMenuOpenDesktop(false);
                                setIsRoleMenuOpenMobile(false);
                            }}
                            className="flex items-center justify-center bg-primary-600 hover:bg-primary-700 text-white font-medium py-2 px-3 rounded-md transition duration-150 ease-in-out text-sm"
                            aria-haspopup="menu"
                            aria-expanded={isOptionsOpen}
                            aria-controls="options-menu"
                        >
                            <span className="mr-1">Options</span>
                            <svg
                                className={`w-4 h-4 transition-transform duration-200 ${isOptionsOpen ? "rotate-180" : ""}`}
                                fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true"
                            >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                        </button>

                        {isOptionsOpen && (
                            <div id="options-menu" className="absolute right-0 mt-2 py-2 w-64 bg-white rounded-md shadow-xl z-20">
                                <div className="px-4 py-2 border-b border-gray-200">
                                    <p className="text-sm font-medium text-gray-500">Region</p>
                                    <div className="mt-1"><RegionSwitcher /></div>
                                </div>
                                <div className="px-4 py-2 border-b border-gray-200">
                                    <p className="text-sm font-medium text-gray-500">Bonus Type</p>
                                    <div className="mt-1">
                                        <BonusTypeSwitcher
                                            onBonusTypeChange={(newType) => {
                                                if (newType !== bonusType) setBonusType(newType);
                                            }}
                                        />
                                    </div>
                                </div>
                                {/* Mobile Role menu */}
                                <div className="px-4 py-2" ref={roleMenuMobileRef}>
                                    <p className="text-sm font-medium text-gray-500">Role</p>
                                    <div className="mt-1">
                                        <button
                                            ref={roleTriggerMobileRef}
                                            onClick={() => {
                                                if (isRoleLoading) return;
                                                lastFocusTargetRef.current = "mobileRole";
                                                setIsRoleMenuOpenMobile((v) => !v);
                                            }}
                                            disabled={isRoleLoading}
                                            className="flex items-center justify-between w-full bg-primary-600 hover:bg-primary-700 text-white font-medium py-2 px-4 rounded-md transition duration-150 ease-in-out disabled:opacity-75 disabled:cursor-not-allowed"
                                            aria-haspopup="menu"
                                            aria-expanded={isRoleMenuOpenMobile}
                                            aria-controls="role-menu-mobile"
                                            aria-busy={isRoleLoading}
                                        >
                                            <RoleLabel isMenuOpen={isRoleMenuOpenMobile} />
                                        </button>

                                        {isRoleMenuOpenMobile && (
                                            <div
                                                id="role-menu-mobile"
                                                ref={roleDropdownMobileRef}
                                                role="menu"
                                                className="mt-1 py-1 w-full bg-white rounded-md border border-gray-200"
                                            >
                                                {filteredRoles.map((r) => (
                                                    <button
                                                        key={r}
                                                        onClick={() => handleRoleSelect(r)}
                                                        onKeyDown={handleMobileRoleKeyDown}
                                                        className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 w-full text-left transition duration-150 ease-in-out"
                                                        role="menuitem"
                                                        tabIndex={-1}
                                                    >
                                                        {r}
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    <SignOutButton />
                </div>
            </div>
        </nav>
    );
};

export default Header;